import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangleIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { vpcApi } from '@nebula/shared/services'
import type { VPCListItem } from '@nebula/shared/services/vpcApi'
import { getApiErrorCode, getErrorMessage } from '@nebula/shared/utils'

/**
 * Modal "Excluir VPC", em dois passos: confirmar, e — se houver recursos
 * conectados — escolher para onde eles vão.
 *
 * Vive fora da NetworkingPage pelo mesmo motivo do `ChangeNetworkModal`: é aqui
 * que mora a decisão que a issue #804 corrigiu. Excluir a VPC não apaga uma
 * linha: MOVE as VMs do cliente para outra rede, com endereço, cloud-init e DHCP
 * junto. As três coisas que a tela precisa fazer certo:
 *
 * - **oferecer só destinos que funcionam** — VPC de outro cluster ou desabilitada
 *   é recusada pelo backend, e listá-la é um 409 esperando acontecer;
 * - **pré-selecionar quando não há escolha** — com uma única alternativa, decidir
 *   por ele é o "automático" que o cliente esperava, sem tirar dele a confirmação;
 * - **não deixar migrar às cegas para uma VPC sem Cloud Gateway** — ali as VMs
 *   ficam sem DHCP e sem internet. Aviso + confirmação explícita, no padrão que a
 *   #727 estabeleceu, em vez do 400 que o portal engolia.
 */

/**
 * O que o modal entrega à página sobre as consequências da operação.
 *
 * Não significa "a exclusão deu certo": os abortos posteriores à migração (troca
 * pendente no Proxmox, VNET que não some) também mudam a rede das VMs, e é ali
 * que mora o aviso de que alguma trocou de endereço fixo. Um aviso desses só
 * existe uma vez — se a tela não o mostrar, ninguém o vê.
 */
export type ExclusaoDeVpcConcluida = {
  /** Sujeito dos avisos: a VM migrada, ou "N VM(s)". Vazio quando não migrou nada. */
  sujeito: string
  /** Nome da VPC que recebeu os recursos. */
  vpcName: string
  /** Vazio no caminho limpo; com itens, o cliente PRECISA lê-los. */
  warnings: string[]
}

type RecursoAssociado = {
  id: string
  instance_name: string
  proxmox_vm_id: number | null
  status: string | null
}

type DeleteVPCModalProps = {
  /** VPC a excluir (null = modal fechado). */
  vpc: VPCListItem | null
  /** Todas as VPCs do cliente — o destino sai daqui, filtrado. */
  vpcs: VPCListItem[]
  onClose: () => void
  /**
   * Obrigatório de propósito: esquecer de tratar seria perder o aviso de que as
   * VMs mudaram de endereço ou ficaram sem DHCP.
   */
  onDeleted: (resultado: ExclusaoDeVpcConcluida) => void
}

export default function DeleteVPCModal({ vpc, vpcs, onClose, onDeleted }: DeleteVPCModalProps) {
  const queryClient = useQueryClient()
  const [confirmName, setConfirmName] = useState('')
  const [passo, setPasso] = useState<'confirm' | 'migrate'>('confirm')
  const [migrateToId, setMigrateToId] = useState<string | null>(null)
  const [vmsAssociadas, setVmsAssociadas] = useState<RecursoAssociado[]>([])
  const [gatewayAssociado, setGatewayAssociado] = useState<{
    id: string
    name: string
  } | null>(null)
  const [confirmNoGateway, setConfirmNoGateway] = useState(false)
  // Avisos que vieram junto de uma RECUSA (a operação falhou, mas as VMs já
  // mudaram de rede). Ficam aqui, dentro do modal que continua aberto, e são
  // entregues à página no fechar: montá-los lá fora agora os colocaria atrás do
  // overlay `fixed inset-0 z-50`, e o `scrollIntoView`/`focus` do banner levaria
  // o teclado para um elemento invisível.
  const [avisosDaRecusa, setAvisosDaRecusa] = useState<string[]>([])
  const [migradasNaRecusa, setMigradasNaRecusa] = useState<string[]>([])

  // VPC de outro cluster nunca funciona (a bridge dela não existe nos nodes onde
  // estas VMs rodam) e a desligada é recusada: oferecê-las é só um 409 esperando
  // acontecer. `enabled` E `is_active`, como o backend exige — filtrar só por uma
  // delas deixa passar um destino que a operação recusa. Sem cluster conhecido
  // não dá para filtrar — melhor listar tudo do que esvaziar a lista.
  const destinosElegiveis = useMemo(
    () =>
      vpcs.filter(
        (candidata) =>
          candidata.id !== vpc?.id &&
          candidata.enabled !== false &&
          candidata.is_active !== false &&
          (!vpc?.cluster_id || candidata.cluster_id === vpc.cluster_id),
      ),
    [vpcs, vpc?.id, vpc?.cluster_id],
  )

  const destinoSelecionado = destinosElegiveis.find((candidata) => candidata.id === migrateToId)

  const deleteMutation = useMutation({
    mutationFn: ({
      vpcId,
      migrateToVpcId,
      confirmar,
    }: {
      vpcId: string
      migrateToVpcId?: string
      confirmar?: boolean
    }) => vpcApi.deleteVPC(vpcId, migrateToVpcId, { confirmNoGateway: confirmar }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer-vpcs'] })
      queryClient.invalidateQueries({ queryKey: ['end-customer-topology'] })
      // A migração mexe em alocação de IP: a aba "IPs" mostraria as da VPC que
      // acabou de deixar de existir.
      queryClient.invalidateQueries({ queryKey: ['ec-ipam-subnets'] })
      const migradas = data.migrated_vms ?? []
      // Zera os avisos de uma tentativa ANTERIOR neste mesmo modal antes de
      // fechar: sem isto o `fechar()` os reemitiria por cima destes, e a página
      // (que guarda só o último) mostraria o aviso da tentativa que falhou em
      // vez do desfecho real. Cada aviso só existe uma vez.
      setAvisosDaRecusa([])
      setMigradasNaRecusa([])
      onDeleted({
        // Sem VM migrada (só o Cloud Gateway mudou de VPC) não há sujeito: um
        // "0 VM(s)" no topo do aviso descreveria uma migração que não houve.
        sujeito:
          migradas.length === 0
            ? ''
            : migradas.length === 1
              ? migradas[0]
              : `${migradas.length} VM(s)`,
        // Nome confirmado pela lista local — a resposta não o repete, e é o
        // destino que o cliente acabou de escolher.
        vpcName: destinoSelecionado?.name ?? '',
        warnings: data.warnings ?? [],
      })
      fechar(true)
    },
    onError: (erro: any) => {
      const detail = erro?.response?.data?.error || erro?.response?.data?.detail
      if (detail?.associated_vms || detail?.associated_gateway) {
        setVmsAssociadas(detail.associated_vms || [])
        setGatewayAssociado(detail.associated_gateway || null)
        setPasso('migrate')
      }
      // Recusa DEPOIS da migração (troca pendente, VNET que não some, PUT
      // recusado no meio): as VMs mudaram de rede de verdade, e o aviso de que
      // alguma trocou de endereço fixo vem junto do erro. Some se ninguém o
      // guardar — a mensagem de erro sozinha não o contém.
      if ((detail?.warnings ?? []).length > 0) {
        setAvisosDaRecusa(detail.warnings)
        setMigradasNaRecusa(detail.migrados ?? [])
      }
    },
  })

  // `has_gateway` pode vir ausente de uma API antiga; nesse caso não há o que
  // afirmar, e inventar "sem gateway" bloquearia o fluxo por engano.
  const destinoSemGateway = destinoSelecionado?.has_gateway === false
  // A lista pode estar velha: se o gateway foi desatachado depois do load,
  // `has_gateway` mente e o checkbox nunca apareceria — o cliente levaria o 409
  // sem saída até recarregar a página. É para isso que o backend devolve `code`
  // estruturado em vez de só texto (#727).
  const recusadoPorFaltaDeGateway = getApiErrorCode(deleteMutation.error) === 'vpc_without_gateway'
  const precisaConfirmarSemGateway = destinoSemGateway || recusadoPorFaltaDeGateway

  // Pré-seleção da única alternativa: com uma VPC só, escolher por ele não é
  // adivinhação — é o "automático" que o cliente esperava (#804). Com duas ou
  // mais o campo continua vazio: mandar as VMs para a rede errada em silêncio é
  // pior que um clique a mais.
  useEffect(() => {
    if (passo !== 'migrate') return
    if (migrateToId) return
    if (destinosElegiveis.length !== 1) return
    setMigrateToId(destinosElegiveis[0].id)
  }, [passo, migrateToId, destinosElegiveis])

  const fechar = (avisosJaEntregues = false) => {
    // Os avisos da recusa sobem agora: a página os mostra ao lado da topologia,
    // sem overlay por cima. Sem isto eles morreriam com o modal. O
    // `avisosJaEntregues` existe porque o `setAvisosDaRecusa([])` do `onSuccess`
    // só vale no próximo render — sem ele, o desfecho real seria sobrescrito
    // aqui pelos avisos da tentativa anterior.
    if (!avisosJaEntregues && avisosDaRecusa.length > 0) {
      onDeleted({
        sujeito: migradasNaRecusa.join(', '),
        vpcName: destinoSelecionado?.name ?? '',
        warnings: avisosDaRecusa,
      })
    }
    setAvisosDaRecusa([])
    setMigradasNaRecusa([])
    setConfirmName('')
    setPasso('confirm')
    setMigrateToId(null)
    setVmsAssociadas([])
    setGatewayAssociado(null)
    setConfirmNoGateway(false)
    deleteMutation.reset()
    onClose()
  }

  // O componente não desmonta ao fechar (devolve `null`): sem isto, o passo de
  // migração de uma exclusão recusada sobreviveria à próxima abertura, sobre
  // outra VPC — com a lista de recursos da anterior.
  useEffect(() => {
    setConfirmName('')
    setPasso('confirm')
    setMigrateToId(null)
    setVmsAssociadas([])
    setGatewayAssociado(null)
    setConfirmNoGateway(false)
    setAvisosDaRecusa([])
    setMigradasNaRecusa([])
    deleteMutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vpc?.id])

  if (!vpc) return null

  const excluindo = deleteMutation.isPending
  const mensagemDeErro = deleteMutation.isError ? getErrorMessage(deleteMutation.error) : null
  const bloqueado =
    excluindo ||
    (passo === 'confirm' && confirmName !== vpc.name) ||
    (passo === 'migrate' && !migrateToId) ||
    // Sem o aval, o backend recusa: deixar o botão ativo faria o cliente clicar
    // num caminho que só devolve o mesmo 409.
    (passo === 'migrate' && precisaConfirmarSemGateway && !confirmNoGateway)

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 p-6">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-red-600">
            <AlertTriangleIcon className="size-5" />
            {passo === 'migrate' ? 'Recursos Conectados' : 'Confirmar Exclusão'}
          </h3>
        </div>

        <div className="space-y-4 p-6">
          {passo === 'migrate' ? (
            <>
              <p className="text-gray-700">
                A VPC <strong>{vpc.name}</strong> possui recursos conectados. Selecione a VPC
                destino para migrar antes de excluir.
              </p>

              <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-800">Recursos que serão migrados:</p>
                {gatewayAssociado && (
                  <div className="flex items-center justify-between text-sm text-amber-900">
                    <span className="font-medium">{gatewayAssociado.name}</span>
                    <span className="text-amber-700">Cloud Gateway</span>
                  </div>
                )}
                {vmsAssociadas.map((vm) => (
                  <div
                    key={vm.id}
                    className="flex items-center justify-between text-sm text-amber-900"
                  >
                    <span className="font-medium">{vm.instance_name}</span>
                    <span className="text-amber-700">
                      VMID {vm.proxmox_vm_id} · {vm.status}
                    </span>
                  </div>
                ))}
              </div>

              <div>
                <label
                  htmlFor="vpc-destino-da-exclusao"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  VPC destino para migração:
                </label>
                <select
                  id="vpc-destino-da-exclusao"
                  value={migrateToId || ''}
                  onChange={(e) => {
                    setMigrateToId(e.target.value || null)
                    // Confirmação é por VPC: trocar o destino exige uma nova.
                    setConfirmNoGateway(false)
                    deleteMutation.reset()
                  }}
                  disabled={excluindo}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Selecione uma VPC...</option>
                  {destinosElegiveis.map((candidata) => (
                    <option key={candidata.id} value={candidata.id}>
                      {candidata.name} ({candidata.cidr})
                      {candidata.has_gateway === false ? ' — sem gateway' : ''}
                    </option>
                  ))}
                </select>
                {destinosElegiveis.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Nenhuma VPC disponível no cluster desta. Crie outra VPC neste cluster antes de
                    excluir.
                  </p>
                )}
              </div>

              {precisaConfirmarSemGateway && (
                <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start space-x-2">
                    <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-600" />
                    <div className="text-sm text-amber-900">
                      <strong>{destinoSelecionado?.name}</strong> não tem Cloud Gateway conectado.
                      As VMs migradas ficam sem DHCP e sem acesso à internet até um gateway ser
                      atachado a essa VPC.
                    </div>
                  </div>
                  <label className="flex items-start space-x-2 text-sm text-amber-900">
                    <input
                      type="checkbox"
                      checked={confirmNoGateway}
                      onChange={(e) => setConfirmNoGateway(e.target.checked)}
                      disabled={excluindo}
                      className="mt-1"
                    />
                    <span>Entendo que as VMs ficarão sem DHCP e sem acesso à internet.</span>
                  </label>
                </div>
              )}

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="text-sm text-blue-900">
                  A migração leva a rede junto: endereço fixo reservado na VPC de destino,
                  configuração de rede reescrita e DHCP do gateway atualizado. Conexões abertas
                  podem cair até as VMs assumirem o endereço na rede nova.
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-gray-700">
                Tem certeza que deseja excluir a VPC <strong>{vpc.name}</strong>?
              </p>

              <div className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Nome</span>
                  <span className="font-medium text-gray-900">{vpc.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">CIDR</span>
                  <span className="font-mono text-gray-900">{vpc.cidr}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">VNI</span>
                  <span className="font-mono text-gray-900">{vpc.vni}</span>
                </div>
              </div>

              {vpc.current_allocations > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm text-amber-800">
                    <strong>{vpc.current_allocations}</strong> IP(s) alocado(s) serão liberados com
                    a exclusão desta VPC.
                  </p>
                </div>
              )}

              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-800">
                  <strong>Atenção:</strong> Esta ação irá remover a rede virtual e pode afetar VMs
                  conectadas a ela.
                </p>
              </div>

              <div>
                <label
                  htmlFor="confirmacao-do-nome-da-vpc"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Digite <strong>{vpc.name}</strong> para confirmar:
                </label>
                <input
                  id="confirmacao-do-nome-da-vpc"
                  type="text"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={vpc.name}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-red-500"
                  autoFocus
                />
              </div>
            </>
          )}

          {mensagemDeErro && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {mensagemDeErro}
            </div>
          )}

          {avisosDaRecusa.length > 0 && (
            // A operação falhou, mas a rede das VMs mudou: estes avisos contam o
            // que já valeu (endereço fixo trocado, destino sem gateway) e não
            // podem esperar o modal fechar para aparecer.
            <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-800">O que já mudou na sua rede:</p>
              {avisosDaRecusa.map((aviso) => (
                <p key={aviso} className="text-sm text-amber-700">
                  • {aviso}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 border-t border-gray-200 p-6">
          <button
            onClick={fechar}
            disabled={excluindo}
            className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() =>
              deleteMutation.mutate(
                passo === 'migrate'
                  ? {
                      vpcId: vpc.id,
                      migrateToVpcId: migrateToId || undefined,
                      confirmar: confirmNoGateway,
                    }
                  : { vpcId: vpc.id },
              )
            }
            disabled={bloqueado}
            className="flex items-center space-x-2 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {excluindo ? (
              <>
                <div className="size-4 animate-spin rounded-full border-b-2 border-white" />
                <span>{passo === 'migrate' ? 'Migrando e excluindo...' : 'Excluindo...'}</span>
              </>
            ) : (
              <>
                <Trash2Icon className="size-4" />
                <span>{passo === 'migrate' ? 'Migrar e Excluir' : 'Excluir VPC'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
